/**
 * Local Database - Account Operations
 *
 * CRUD operations for accounts.
 */

import { initializeLocalDb } from './init'
import {
  localStore,
  isElectron,
  getElectronWindow,
  saveToLocalStorage,
} from './store'

import type { LocalAccount } from './types'
import type { AccountType } from '../../constants/labels'

/**
 * Get all accounts from local database
 * @param orgId Organization ID
 * @param ledgerId Optional ledger ID to filter by (defaults to primary ledger)
 */
export async function getLocalAccounts(orgId: string, ledgerId?: string): Promise<LocalAccount[]> {
  if (isElectron) {
    return getElectronWindow().electronAPI.db.getAccounts(orgId, ledgerId)
  }

  // Initialize if needed
  /* v8 ignore next 3 */
  if (!localStore.initialized) {
    await initializeLocalDb(orgId)
  }

  const targetLedgerId = ledgerId ?? localStore.defaultLedgerId

  return Array.from(localStore.accounts.values())
    .filter(a => a.orgId === orgId && (!targetLedgerId || a.ledgerId === targetLedgerId))
    .sort((a, b) => a.code.localeCompare(b.code))
}

/**
 * Get accounts grouped by type
 */
export async function getLocalAccountsByType(orgId: string, ledgerId?: string): Promise<Record<AccountType, LocalAccount[]>> {
  const accounts = await getLocalAccounts(orgId, ledgerId)

  const grouped: Record<AccountType, LocalAccount[]> = {
    ACCOUNT_TYPE_ASSET: [],
    ACCOUNT_TYPE_LIABILITY: [],
    ACCOUNT_TYPE_EQUITY: [],
    ACCOUNT_TYPE_REVENUE: [],
    ACCOUNT_TYPE_EXPENSE: [],
  }

  for (const account of accounts) {
    if (!account.isHeader) {
      grouped[account.accountType].push(account)
    }
  }

  return grouped
}

/**
 * Get account by ID
 */
export async function getLocalAccount(orgId: string, accountId: string): Promise<LocalAccount | null> {
  if (isElectron) {
    return getElectronWindow().electronAPI.db.getAccount(orgId, accountId)
  }

  const account = localStore.accounts.get(accountId)
  return account?.orgId === orgId ? account : null
}

/**
 * Get account by member ID
 * Returns the account linked to a specific member entity
 */
export async function getLocalAccountByMemberId(orgId: string, memberId: string): Promise<LocalAccount | null> {
  const accounts = await getLocalAccounts(orgId)
  return accounts.find(a => a.memberId === memberId) ?? null
}

/**
 * Get all accounts linked to members
 * Optionally filter by account type
 */
export async function getLocalMemberAccounts(
  orgId: string,
  accountType?: AccountType
): Promise<LocalAccount[]> {
  const accounts = await getLocalAccounts(orgId)
  return accounts.filter(a =>
    a.memberId !== undefined &&
    (!accountType || a.accountType === accountType)
  )
}

/**
 * Create a new account
 * @param serverId Optional server-assigned ID to use instead of generating a new UUID
 */
export async function createLocalAccount(
  orgId: string,
  data: Omit<LocalAccount, 'id' | 'orgId' | 'createdAt' | 'updatedAt'>,
  serverId?: string
): Promise<LocalAccount> {
  const id = serverId ?? crypto.randomUUID()
  const now = new Date().toISOString()

  const account: LocalAccount = {
    ...data,
    id,
    orgId,
    createdAt: now,
    updatedAt: now,
  }

  if (isElectron) {
    return getElectronWindow().electronAPI.db.createAccount(account)
  }

  localStore.accounts.set(id, account)
  saveToLocalStorage()
  return account
}
